import {
  LucideIcon,
  Scale,
  TrendingUp,
  Stethoscope,
  Pill,
  Shield,
  HardHat,
  Users,
  Briefcase,
  FileText,
  Clock,
  AlertCircle,
  Search,
  PenTool,
  Lightbulb,
  BarChart3,
  Target,
  BookOpen,
  Gavel,
  Calculator,
  ClipboardCheck,
  FolderOpen,
  FileCheck,
  MessageSquare,
  CheckCircle,
} from 'lucide-react'

export interface AIAgent {
  name: string
  role: string
  description: string
  icon: LucideIcon
  color: string
  catchphrase: string
}

export interface Workflow {
  name: string
  trigger: string
  steps: string[]
  output: string
  timeSaved: string
}

export interface Template {
  name: string
  type: 'Word' | 'Excel' | 'PowerPoint' | 'PDF'
  description: string
}

export interface ROIMetric {
  metric: string
  before: string
  after: string
  improvement: string
}

export interface UserPersona {
  name: string
  role: string
  description: string
  timeSaved: string
}

/**
 * Page composition for an industry page. Each page in the industries family
 * has its own layout (see pages/industries/layouts/).
 */
export type IndustryLayout =
  | 'blogs-listing'
  | 'stats-hero'
  | 'statement'
  | 'card-tornado'
  | 'giant-word'
  | 'dock-marquee'

export interface IndustryData {
  slug: string
  /** Which page layout renders this industry. */
  layout: IndustryLayout
  name: string
  /** One-word display name for the hero highlight box. */
  shortName: string
  icon: LucideIcon
  color: string
  gradient: string
  tier: 1 | 2 | 3

  // SEO
  metaTitle: string
  metaDescription: string

  // Hero
  tagline: string
  headline: string
  subheadline: string
  heroStats: Array<{ value: string; label: string }>

  // Overview
  marketSize: string
  documentIntensity: string
  complianceRequirements: string[]

  // Pain Points
  painPoints: Array<{
    title: string
    impact: string
    solution: string
    icon: LucideIcon
  }>

  // AI Agents
  aiAgents: AIAgent[]

  // Workflows
  workflows: Workflow[]

  // Templates
  templates: Template[]

  // ROI
  roiMetrics: ROIMetric[]

  // Sample Company
  sampleCompany: {
    name: string
    type: string
    size: string
    structure: string[]
  }

  // User Personas
  personas: UserPersona[]

  // CTA
  ctaHeadline: string
  ctaDescription: string
}

export const industriesData: Record<string, IndustryData> = {
  legal: {
    slug: 'legal',
    layout: 'blogs-listing',
    name: 'Legal',
    shortName: 'Legal',
    icon: Scale,
    color: '#2563eb',
    gradient: 'from-blue-600 to-indigo-700',
    tier: 1,

    metaTitle: 'Legal Document Automation | Templatean for Law Firms',
    metaDescription: 'AI-powered contract review, brief preparation, and compliance checking. Save 83% on contract review time. Built for law firms and legal departments.',

    tagline: 'For Legal Teams',
    headline: 'Every Contract Reviewed. Every Brief Polished. Every Deadline Met.',
    subheadline: 'AI agents work together to handle contract review, legal research, compliance checking, and client communications—all within Word, Excel, and Outlook.',
    heroStats: [
      { value: '83%', label: 'Faster contract review' },
      { value: '63%', label: 'Faster brief prep' },
      { value: '90%', label: 'Fewer compliance errors' },
    ],

    marketSize: '$350 billion (US legal services)',
    documentIntensity: 'Very High (50-100 docs per attorney per month)',
    complianceRequirements: ['Bar rules', 'Court procedures', 'Regulatory filings', 'Client confidentiality'],

    painPoints: [
      {
        title: 'Contract Review Takes Too Long',
        impact: '60-90 minutes per contract',
        solution: 'AI extracts key terms in 5 minutes',
        icon: Clock,
      },
      {
        title: 'Version Control Chaos',
        impact: 'Wrong versions sent to clients',
        solution: 'Automatic versioning and tracking',
        icon: AlertCircle,
      },
      {
        title: 'Partner Review Bottleneck',
        impact: '2-3 day delays for review',
        solution: 'AI pre-review reduces partner time 70%',
        icon: Users,
      },
    ],

    aiAgents: [
      {
        name: 'Clara',
        role: 'Contract Analyst',
        description: 'Extracts key terms, identifies risks, compares versions, tracks deadlines',
        icon: Search,
        color: '#3B82F6',
        catchphrase: 'Let me review that contract for you',
      },
      {
        name: 'Justice',
        role: 'Compliance Guardian',
        description: 'Checks regulatory requirements, verifies citations, ensures disclosures',
        icon: Shield,
        color: '#EF4444',
        catchphrase: 'I\'ll make sure this is compliant',
      },
      {
        name: 'Lexis',
        role: 'Legal Researcher',
        description: 'Finds case law, researches statutes, formats citations per Bluebook',
        icon: BookOpen,
        color: '#8B5CF6',
        catchphrase: 'I found relevant precedents',
      },
      {
        name: 'Atticus',
        role: 'Legal Writer',
        description: 'Drafts contracts, writes briefs, composes correspondence',
        icon: PenTool,
        color: '#F59E0B',
        catchphrase: 'Here\'s your draft',
      },
      {
        name: 'Parker',
        role: 'Matter Manager',
        description: 'Organizes documents, tracks versions, manages deadlines',
        icon: FolderOpen,
        color: '#F97316',
        catchphrase: 'Everything is organized',
      },
      {
        name: 'Bailey',
        role: 'Client Communicator',
        description: 'Drafts updates, explains concepts in plain language',
        icon: MessageSquare,
        color: '#14B8A6',
        catchphrase: 'Your client update is ready',
      },
    ],

    workflows: [
      {
        name: 'Contract Review Pipeline',
        trigger: 'New contract uploaded to matter folder',
        steps: ['Clara extracts terms', 'Clara identifies risks', 'Justice checks compliance', 'Atticus drafts response memo'],
        output: 'Risk summary with recommended actions',
        timeSaved: '15 minutes (vs. 90 minutes manual)',
      },
      {
        name: 'Brief Preparation',
        trigger: 'Litigation brief assignment created',
        steps: ['Lexis researches authorities', 'Atticus creates outline', 'Atticus drafts sections', 'Justice verifies citations'],
        output: 'Court-ready brief with proper citations',
        timeSaved: '3 hours (vs. 8 hours manual)',
      },
      {
        name: 'M&A Due Diligence',
        trigger: 'New deal initiated',
        steps: ['Parker organizes data room', 'Clara reviews all contracts', 'Justice checks regulatory compliance', 'Atticus drafts DD report'],
        output: 'Complete due diligence report',
        timeSaved: '2 days (vs. 2 weeks manual)',
      },
      {
        name: 'Client Status Report',
        trigger: 'Weekly schedule or manual request',
        steps: ['Parker gathers matter status', 'Bailey drafts update', 'Human Review', 'Send'],
        output: 'Professional client update letter',
        timeSaved: '20 minutes (vs. 60 minutes manual)',
      },
    ],

    templates: [
      { name: 'Standard NDA (Mutual)', type: 'Word', description: 'Bilateral confidentiality agreement' },
      { name: 'Master Services Agreement', type: 'Word', description: 'Professional services contract' },
      { name: 'Engagement Letter', type: 'Word', description: 'New client/matter onboarding' },
      { name: 'Legal Brief Template', type: 'Word', description: 'Court filing format' },
      { name: 'Client Memorandum', type: 'Word', description: 'Legal advice format' },
      { name: 'Closing Checklist', type: 'Excel', description: 'Transaction tracking' },
      { name: 'Demand Letter', type: 'Word', description: 'Formal legal demand' },
      { name: 'Settlement Agreement', type: 'Word', description: 'Dispute resolution' },
    ],

    roiMetrics: [
      { metric: 'Contract review time', before: '90 minutes', after: '15 minutes', improvement: '83% faster' },
      { metric: 'Brief preparation', before: '8 hours', after: '3 hours', improvement: '63% faster' },
      { metric: 'Junior associate docs/day', before: '4 documents', after: '12 documents', improvement: '3x increase' },
      { metric: 'Compliance errors', before: '5%/month', after: '0.5%/month', improvement: '90% reduction' },
    ],

    sampleCompany: {
      name: 'Morrison & Sterling LLP',
      type: 'Mid-size law firm',
      size: '150 attorneys + 80 staff',
      structure: ['Corporate/M&A (47 attorneys)', 'Litigation (40 attorneys)', 'Real Estate (29 attorneys)', 'IP (20 attorneys)', 'Employment (14 attorneys)'],
    },

    personas: [
      { name: 'Robert Chen', role: 'Managing Partner', description: 'Reviews AI summaries, approves major documents', timeSaved: '5 hours/week' },
      { name: 'Jennifer Martinez', role: 'Senior Associate', description: 'Manages workflows, reviews junior drafts', timeSaved: '12 hours/week' },
      { name: 'David Park', role: 'Junior Associate', description: 'Heavy AI assistance for drafting and research', timeSaved: '15 hours/week' },
      { name: 'Maria Santos', role: 'Paralegal', description: 'Template filling, compliance checking', timeSaved: '10 hours/week' },
    ],

    ctaHeadline: 'Ready to transform your legal practice?',
    ctaDescription: 'See how 150-attorney firms save $2.1M annually with Templatean\'s AI document team.',
  },

  finance: {
    slug: 'finance',
    layout: 'stats-hero',
    name: 'Finance & Accounting',
    shortName: 'Finance',
    icon: TrendingUp,
    color: '#059669',
    gradient: 'from-emerald-600 to-teal-700',
    tier: 1,

    metaTitle: 'Financial Document Automation | Templatean for Finance Teams',
    metaDescription: 'AI-powered financial analysis, compliance checking, and report generation. 85% faster quarterly reports. Built for investment advisors and finance departments.',

    tagline: 'For Finance Teams',
    headline: 'Every Report Accurate. Every Model Documented. Every Filing Compliant.',
    subheadline: 'AI agents handle financial analysis, valuation modeling, audit compliance, and investor reporting—all with complete audit trails.',
    heroStats: [
      { value: '85%', label: 'Faster quarterly reports' },
      { value: '75%', label: 'Faster compliance review' },
      { value: '93%', label: 'Reduction in data errors' },
    ],

    marketSize: '$540 billion (US financial services)',
    documentIntensity: 'Very High (complex, data-heavy documents)',
    complianceRequirements: ['SEC', 'FINRA', 'SOX', 'GAAP', 'IFRS'],

    painPoints: [
      {
        title: 'Complex Report Preparation',
        impact: '2 weeks for quarterly reports',
        solution: 'AI generates reports in 2 days',
        icon: Clock,
      },
      {
        title: 'Manual Compliance Checking',
        impact: '4 hours per document',
        solution: 'Automated compliance verification',
        icon: ClipboardCheck,
      },
      {
        title: 'Data Entry Errors',
        impact: '3% error rate monthly',
        solution: 'AI validation reduces to 0.2%',
        icon: AlertCircle,
      },
    ],

    aiAgents: [
      {
        name: 'Marcus',
        role: 'Financial Analyst',
        description: 'Ratio analysis, trend identification, variance analysis',
        icon: BarChart3,
        color: '#3B82F6',
        catchphrase: 'Here\'s the financial analysis',
      },
      {
        name: 'Victoria',
        role: 'Valuation Expert',
        description: 'DCF modeling, comparable analysis, sensitivity analysis',
        icon: Calculator,
        color: '#EF4444',
        catchphrase: 'I\'ve prepared the valuation',
      },
      {
        name: 'Audrey',
        role: 'Audit & Compliance',
        description: 'SOX compliance, control testing, regulatory verification',
        icon: Shield,
        color: '#8B5CF6',
        catchphrase: 'Compliance verified',
      },
      {
        name: 'Preston',
        role: 'Presentation Builder',
        description: 'Charts, executive summaries, board decks',
        icon: Target,
        color: '#F59E0B',
        catchphrase: 'Your deck is ready',
      },
      {
        name: 'Quentin',
        role: 'Quantitative Analyst',
        description: 'Statistical analysis, forecasting, model validation',
        icon: Lightbulb,
        color: '#F97316',
        catchphrase: 'I\'ve run the numbers',
      },
      {
        name: 'Beatrice',
        role: 'Report Writer',
        description: 'Earnings reports, MD&A sections, regulatory narratives',
        icon: PenTool,
        color: '#14B8A6',
        catchphrase: 'Report narrative complete',
      },
    ],

    workflows: [
      {
        name: 'Quarterly Investor Report',
        trigger: 'End of quarter',
        steps: ['Marcus analyzes performance', 'Audrey checks compliance', 'Preston creates charts', 'Beatrice writes narrative'],
        output: 'Complete quarterly investor report',
        timeSaved: '2 days (vs. 2 weeks manual)',
      },
      {
        name: 'Investment Committee Memo',
        trigger: 'New deal evaluation',
        steps: ['Victoria builds valuation model', 'Marcus runs analysis', 'Quentin performs risk assessment', 'Beatrice drafts memo'],
        output: 'Investment recommendation memo',
        timeSaved: '1.5 hours (vs. 6 hours manual)',
      },
      {
        name: 'Board Presentation',
        trigger: 'Board meeting scheduled',
        steps: ['Marcus gathers metrics', 'Preston builds deck', 'Audrey reviews compliance', 'Final review'],
        output: 'Board-ready presentation',
        timeSaved: '4 hours (vs. 12 hours manual)',
      },
      {
        name: 'Audit Response Package',
        trigger: 'Audit request received',
        steps: ['Audrey identifies requirements', 'Marcus pulls supporting data', 'Beatrice drafts responses', 'Package review'],
        output: 'Complete audit response',
        timeSaved: '1 day (vs. 4 days manual)',
      },
    ],

    templates: [
      { name: 'Quarterly Investor Report', type: 'Word', description: 'Fund performance and commentary' },
      { name: 'Investment Committee Memo', type: 'Word', description: 'Deal recommendation and analysis' },
      { name: 'Financial Model Template', type: 'Excel', description: 'DCF, LBO, merger model structures' },
      { name: 'Board Presentation', type: 'PowerPoint', description: 'Quarterly board meeting deck' },
      { name: 'Audit Findings Report', type: 'Word', description: 'Internal audit results' },
      { name: 'Due Diligence Checklist', type: 'Excel', description: 'Investment due diligence tracker' },
      { name: 'Risk Assessment Report', type: 'Word', description: 'Portfolio risk analysis' },
      { name: 'Fee Disclosure Document', type: 'Word', description: 'Client fee transparency' },
    ],

    roiMetrics: [
      { metric: 'Quarterly report prep', before: '2 weeks', after: '2 days', improvement: '85% faster' },
      { metric: 'Investment memo creation', before: '6 hours', after: '1.5 hours', improvement: '75% faster' },
      { metric: 'Compliance review', before: '4 hours/doc', after: '1 hour/doc', improvement: '75% faster' },
      { metric: 'Data entry errors', before: '3%/month', after: '0.2%/month', improvement: '93% reduction' },
    ],

    sampleCompany: {
      name: 'Meridian Capital Advisors',
      type: 'Registered Investment Advisor',
      size: '75 employees, $2.5B AUM',
      structure: ['Investment Team (27)', 'Client Services (15)', 'Compliance & Legal (8)', 'Operations (20)'],
    },

    personas: [
      { name: 'Michael Roberts', role: 'Portfolio Manager', description: 'Reviews AI summaries, approves client reports', timeSaved: '8 hours/week' },
      { name: 'Sarah Kim', role: 'Research Analyst', description: 'Uses AI for modeling and research reports', timeSaved: '15 hours/week' },
      { name: 'James Wilson', role: 'Compliance Officer', description: 'Monitors documents for regulatory compliance', timeSaved: '10 hours/week' },
      { name: 'Emily Chen', role: 'Client Advisor', description: 'Creates presentations and performance summaries', timeSaved: '12 hours/week' },
    ],

    ctaHeadline: 'Ready to transform your financial operations?',
    ctaDescription: 'See how RIAs save $40,000 per user annually with Templatean\'s AI document team.',
  },

  healthcare: {
    slug: 'healthcare',
    layout: 'statement',
    name: 'Healthcare',
    shortName: 'Healthcare',
    icon: Stethoscope,
    color: '#dc2626',
    gradient: 'from-red-600 to-rose-700',
    tier: 1,

    metaTitle: 'Healthcare Document Automation | Templatean for Healthcare',
    metaDescription: 'HIPAA-compliant AI document automation. PHI detection, clinical documentation, and research support. Built for healthcare organizations.',

    tagline: 'For Healthcare Organizations',
    headline: 'Every Document HIPAA-Compliant. Every Protocol Clear. Every Patient Protected.',
    subheadline: 'AI agents handle compliance checking, clinical documentation, research support, and patient communications—with full PHI protection.',
    heroStats: [
      { value: '100%', label: 'HIPAA compliance' },
      { value: '60%', label: 'Faster documentation' },
      { value: '80%', label: 'Fewer compliance gaps' },
    ],

    marketSize: '$4.5 trillion (US healthcare)',
    documentIntensity: 'Very High (clinical + administrative)',
    complianceRequirements: ['HIPAA', 'Joint Commission', 'CMS', 'State regulations'],

    painPoints: [
      {
        title: 'HIPAA Compliance Risk',
        impact: 'PHI exposure in documents',
        solution: 'AI detects and redacts PHI automatically',
        icon: Shield,
      },
      {
        title: 'Clinical Documentation Burden',
        impact: 'Hours spent on paperwork',
        solution: 'AI assists with formatting and coding',
        icon: Clock,
      },
      {
        title: 'Research Protocol Complexity',
        impact: 'Weeks for IRB submissions',
        solution: 'AI helps draft and format protocols',
        icon: FileText,
      },
    ],

    aiAgents: [
      {
        name: 'HIPPA',
        role: 'Compliance Guardian',
        description: 'PHI detection, HIPAA verification, audit documentation',
        icon: Shield,
        color: '#EF4444',
        catchphrase: 'PHI protected and compliant',
      },
      {
        name: 'Florence',
        role: 'Clinical Documentation',
        description: 'Discharge summaries, ICD-10 codes, care plans',
        icon: FileText,
        color: '#3B82F6',
        catchphrase: 'Documentation formatted correctly',
      },
      {
        name: 'Darwin',
        role: 'Research Assistant',
        description: 'Literature review, protocol drafting, IRB prep',
        icon: BookOpen,
        color: '#8B5CF6',
        catchphrase: 'Research summary ready',
      },
      {
        name: 'Galen',
        role: 'Medication Review',
        description: 'Drug interaction flagging, patient education materials',
        icon: Pill,
        color: '#F59E0B',
        catchphrase: 'Medication check complete',
      },
      {
        name: 'Watson',
        role: 'Healthcare Analytics',
        description: 'Quality metrics, outcome tracking, population health',
        icon: BarChart3,
        color: '#F97316',
        catchphrase: 'Quality metrics analyzed',
      },
      {
        name: 'Clara',
        role: 'Patient Communication',
        description: 'Appointment letters, care instructions, test explanations',
        icon: MessageSquare,
        color: '#14B8A6',
        catchphrase: 'Patient communication ready',
      },
    ],

    workflows: [
      {
        name: 'Discharge Documentation',
        trigger: 'Patient discharge initiated',
        steps: ['Florence formats summary', 'HIPPA checks compliance', 'Galen reviews medications', 'Clara prepares patient instructions'],
        output: 'Complete discharge package',
        timeSaved: '30 minutes (vs. 90 minutes manual)',
      },
      {
        name: 'Research Protocol Submission',
        trigger: 'New study planned',
        steps: ['Darwin reviews literature', 'Darwin drafts protocol', 'HIPPA ensures compliance', 'Format for IRB'],
        output: 'IRB-ready protocol package',
        timeSaved: '1 week (vs. 3 weeks manual)',
      },
      {
        name: 'Quality Reporting',
        trigger: 'Monthly/quarterly cycle',
        steps: ['Watson gathers metrics', 'Watson analyzes trends', 'Florence documents findings', 'HIPPA reviews report'],
        output: 'Compliance-ready quality report',
        timeSaved: '2 days (vs. 1 week manual)',
      },
      {
        name: 'Patient Education Package',
        trigger: 'Diagnosis or procedure scheduled',
        steps: ['Galen identifies medication needs', 'Clara writes plain language explanations', 'HIPPA verifies compliance', 'Package assembly'],
        output: 'Patient-ready education materials',
        timeSaved: '20 minutes (vs. 60 minutes manual)',
      },
    ],

    templates: [
      { name: 'Discharge Summary', type: 'Word', description: 'Patient discharge documentation' },
      { name: 'Informed Consent', type: 'Word', description: 'Procedure consent with PHI handling' },
      { name: 'Care Plan Template', type: 'Word', description: 'Treatment plan documentation' },
      { name: 'Quality Report', type: 'Excel', description: 'Quality metrics dashboard' },
      { name: 'Research Protocol', type: 'Word', description: 'Clinical study protocol template' },
      { name: 'Patient Education', type: 'Word', description: 'Condition-specific instructions' },
      { name: 'HIPAA Authorization', type: 'Word', description: 'PHI release authorization' },
      { name: 'Incident Report', type: 'Word', description: 'Safety event documentation' },
    ],

    roiMetrics: [
      { metric: 'Documentation time', before: '90 minutes', after: '30 minutes', improvement: '67% faster' },
      { metric: 'Compliance gaps', before: '15/month', after: '3/month', improvement: '80% reduction' },
      { metric: 'Research protocol prep', before: '3 weeks', after: '1 week', improvement: '67% faster' },
      { metric: 'Patient communication time', before: '60 minutes', after: '20 minutes', improvement: '67% faster' },
    ],

    sampleCompany: {
      name: 'Regional Medical Center Network',
      type: 'Healthcare system',
      size: '4,500 employees (250 using Templatean)',
      structure: ['Administrative Staff (150)', 'Clinical Documentation (45)', 'Research (25)', 'Compliance (15)'],
    },

    personas: [
      { name: 'Dr. Sarah Chen', role: 'Chief Medical Officer', description: 'Reviews clinical documentation standards', timeSaved: '4 hours/week' },
      { name: 'Mark Thompson', role: 'Compliance Officer', description: 'Monitors HIPAA compliance across documents', timeSaved: '10 hours/week' },
      { name: 'Lisa Rodriguez', role: 'Research Coordinator', description: 'Uses AI for protocol and IRB preparation', timeSaved: '12 hours/week' },
      { name: 'James Wilson', role: 'Clinical Doc Specialist', description: 'AI assists with discharge summaries', timeSaved: '15 hours/week' },
    ],

    ctaHeadline: 'Ready to transform healthcare documentation?',
    ctaDescription: 'See how healthcare systems achieve 100% HIPAA compliance with Templatean\'s AI document team.',
  },

  insurance: {
    slug: 'insurance',
    layout: 'card-tornado',
    name: 'Insurance',
    shortName: 'Insurance',
    icon: Shield,
    color: '#7c3aed',
    gradient: 'from-violet-600 to-purple-700',
    tier: 1,

    metaTitle: 'Insurance Document Automation | Templatean for Insurance',
    metaDescription: 'AI-powered policy creation, claims processing, and regulatory compliance. 70% faster underwriting. Built for P&C insurers.',

    tagline: 'For Insurance Companies',
    headline: 'Every Policy Accurate. Every Claim Processed. Every Filing Compliant.',
    subheadline: 'AI agents handle underwriting, claims analysis, compliance checking, and policyholder communications—across all lines of business.',
    heroStats: [
      { value: '70%', label: 'Faster underwriting' },
      { value: '50%', label: 'Faster claims processing' },
      { value: '85%', label: 'Fewer policy errors' },
    ],

    marketSize: '$6 trillion (global insurance)',
    documentIntensity: 'Very High (policies, claims, filings)',
    complianceRequirements: ['State insurance regulations', 'NAIC', 'Federal requirements'],

    painPoints: [
      {
        title: 'Slow Underwriting Process',
        impact: 'Days to issue quotes',
        solution: 'AI accelerates risk assessment',
        icon: Clock,
      },
      {
        title: 'Claims Documentation Backlog',
        impact: 'Weeks to process claims',
        solution: 'AI assists with claim documentation',
        icon: FileText,
      },
      {
        title: 'Regulatory Filing Complexity',
        impact: 'Multiple state requirements',
        solution: 'AI ensures multi-state compliance',
        icon: Gavel,
      },
    ],

    aiAgents: [
      {
        name: 'Lloyd',
        role: 'Underwriter Assistant',
        description: 'Application review, risk assessment, quote generation',
        icon: Search,
        color: '#3B82F6',
        catchphrase: 'Risk assessment complete',
      },
      {
        name: 'Hartford',
        role: 'Claims Analyst',
        description: 'Claims review, investigation reports, settlements',
        icon: FileCheck,
        color: '#EF4444',
        catchphrase: 'Claim documentation ready',
      },
      {
        name: 'Prudence',
        role: 'Compliance Officer',
        description: 'Regulatory filings, policy form compliance, audits',
        icon: Shield,
        color: '#8B5CF6',
        catchphrase: 'Filing requirements verified',
      },
      {
        name: 'Sterling',
        role: 'Actuary Assistant',
        description: 'Loss ratio reporting, reserve documentation',
        icon: Calculator,
        color: '#F59E0B',
        catchphrase: 'Statistical analysis complete',
      },
      {
        name: 'Brooks',
        role: 'Policy Writer',
        description: 'Policy drafting, endorsements, coverage explanations',
        icon: PenTool,
        color: '#F97316',
        catchphrase: 'Policy form drafted',
      },
      {
        name: 'Grace',
        role: 'Client Services',
        description: 'Renewal notices, certificates, correspondence',
        icon: MessageSquare,
        color: '#14B8A6',
        catchphrase: 'Client communication sent',
      },
    ],

    workflows: [
      {
        name: 'New Business Underwriting',
        trigger: 'Application received',
        steps: ['Lloyd reviews application', 'Lloyd assesses risk', 'Sterling runs loss projections', 'Brooks drafts quote'],
        output: 'Complete quote package',
        timeSaved: '4 hours (vs. 1 day manual)',
      },
      {
        name: 'Claims Processing',
        trigger: 'First notice of loss',
        steps: ['Hartford documents claim', 'Hartford reviews coverage', 'Sterling estimates reserve', 'Prudence checks compliance'],
        output: 'Claims decision package',
        timeSaved: '1 day (vs. 3 days manual)',
      },
      {
        name: 'Policy Renewal',
        trigger: '60 days before expiration',
        steps: ['Lloyd reviews loss history', 'Sterling calculates renewal pricing', 'Brooks prepares documents', 'Grace sends notice'],
        output: 'Renewal offer package',
        timeSaved: '2 hours (vs. 6 hours manual)',
      },
      {
        name: 'Regulatory Filing',
        trigger: 'Filing deadline approaching',
        steps: ['Prudence identifies requirements', 'Brooks drafts filings', 'Sterling provides data', 'Compliance review'],
        output: 'State-ready filing package',
        timeSaved: '1 day (vs. 4 days manual)',
      },
    ],

    templates: [
      { name: 'Policy Application', type: 'Word', description: 'Coverage request form' },
      { name: 'Coverage Quote', type: 'Excel', description: 'Premium calculation' },
      { name: 'Claims First Notice', type: 'Word', description: 'Loss notification' },
      { name: 'Certificate of Insurance', type: 'Word', description: 'Proof of coverage' },
      { name: 'Underwriting Report', type: 'Word', description: 'Risk assessment' },
      { name: 'Renewal Notice', type: 'Word', description: 'Policy renewal communication' },
      { name: 'Claims Settlement', type: 'Word', description: 'Settlement documentation' },
      { name: 'Regulatory Filing', type: 'Word', description: 'State filing template' },
    ],

    roiMetrics: [
      { metric: 'Underwriting time', before: '1 day', after: '4 hours', improvement: '70% faster' },
      { metric: 'Claims processing', before: '3 days', after: '1 day', improvement: '67% faster' },
      { metric: 'Policy errors', before: '8%', after: '1.2%', improvement: '85% reduction' },
      { metric: 'Regulatory filing prep', before: '4 days', after: '1 day', improvement: '75% faster' },
    ],

    sampleCompany: {
      name: 'Guardian Insurance Group',
      type: 'Regional P&C insurer',
      size: '600 employees (200 using Templatean)',
      structure: ['Underwriting (60)', 'Claims (80)', 'Compliance (20)', 'Client Services (40)'],
    },

    personas: [
      { name: 'Robert Martinez', role: 'Chief Underwriter', description: 'Reviews AI risk assessments, approves complex risks', timeSaved: '8 hours/week' },
      { name: 'Jennifer Lee', role: 'Claims Supervisor', description: 'Uses AI for claims documentation review', timeSaved: '12 hours/week' },
      { name: 'David Chen', role: 'Compliance Director', description: 'Monitors regulatory filings across states', timeSaved: '10 hours/week' },
      { name: 'Sarah Wilson', role: 'Underwriter', description: 'AI assists with application processing', timeSaved: '15 hours/week' },
    ],

    ctaHeadline: 'Ready to transform insurance operations?',
    ctaDescription: 'See how insurers save $35,000 per user annually with Templatean\'s AI document team.',
  },

  engineering: {
    slug: 'engineering',
    layout: 'giant-word',
    name: 'Engineering & Construction',
    shortName: 'Engineering',
    icon: HardHat,
    color: '#ea580c',
    gradient: 'from-orange-600 to-amber-700',
    tier: 2,

    metaTitle: 'Engineering Document Automation | Templatean for Engineering',
    metaDescription: 'AI-powered specifications, proposals, and project documentation. 65% faster RFI responses. Built for engineering and construction firms.',

    tagline: 'For Engineering Firms',
    headline: 'Every Spec Accurate. Every Proposal Compelling. Every Project Documented.',
    subheadline: 'AI agents handle technical writing, quality review, data analysis, and project documentation—meeting strict industry standards.',
    heroStats: [
      { value: '65%', label: 'Faster RFI responses' },
      { value: '50%', label: 'Faster proposal creation' },
      { value: '75%', label: 'Fewer spec errors' },
    ],

    marketSize: '$1.3 trillion (US construction/engineering)',
    documentIntensity: 'High (technical documentation)',
    complianceRequirements: ['Building codes', 'OSHA', 'Professional licensing', 'Project specs'],

    painPoints: [
      {
        title: 'Specification Writing Time',
        impact: 'Days per specification',
        solution: 'AI assists with technical drafting',
        icon: Clock,
      },
      {
        title: 'RFI Response Delays',
        impact: 'Project schedule impacts',
        solution: 'AI accelerates response generation',
        icon: AlertCircle,
      },
      {
        title: 'Proposal Turnaround',
        impact: 'Lost bid opportunities',
        solution: 'AI generates proposals faster',
        icon: FileText,
      },
    ],

    aiAgents: [
      {
        name: 'Tesla',
        role: 'Technical Writer',
        description: 'Specifications, technical manuals, system descriptions',
        icon: PenTool,
        color: '#3B82F6',
        catchphrase: 'Specification drafted',
      },
      {
        name: 'Newton',
        role: 'Quality Reviewer',
        description: 'Technical accuracy, standards compliance, consistency',
        icon: CheckCircle,
        color: '#EF4444',
        catchphrase: 'Quality check complete',
      },
      {
        name: 'Pascal',
        role: 'Data Analyst',
        description: 'Test data, performance metrics, trend analysis',
        icon: BarChart3,
        color: '#8B5CF6',
        catchphrase: 'Data analysis ready',
      },
      {
        name: 'Ada',
        role: 'Project Documentation',
        description: 'Project plans, status reports, RFI responses',
        icon: FolderOpen,
        color: '#F59E0B',
        catchphrase: 'Project docs updated',
      },
      {
        name: 'Edison',
        role: 'Process Documentation',
        description: 'SOPs, work instructions, safety procedures',
        icon: ClipboardCheck,
        color: '#F97316',
        catchphrase: 'Procedure documented',
      },
      {
        name: 'Curie',
        role: 'Proposal Writer',
        description: 'Technical proposals, RFP responses, qualifications',
        icon: Target,
        color: '#14B8A6',
        catchphrase: 'Proposal ready for review',
      },
    ],

    workflows: [
      {
        name: 'RFI Response',
        trigger: 'RFI received',
        steps: ['Ada logs RFI', 'Tesla drafts response', 'Newton reviews accuracy', 'Submit response'],
        output: 'Accurate RFI response',
        timeSaved: '2 hours (vs. 6 hours manual)',
      },
      {
        name: 'Technical Proposal',
        trigger: 'RFP received',
        steps: ['Curie analyzes requirements', 'Tesla drafts technical sections', 'Newton reviews', 'Final package'],
        output: 'Complete technical proposal',
        timeSaved: '2 days (vs. 1 week manual)',
      },
      {
        name: 'Project Status Report',
        trigger: 'Monthly cycle',
        steps: ['Ada gathers project data', 'Pascal analyzes metrics', 'Tesla writes narrative', 'Review and send'],
        output: 'Client-ready status report',
        timeSaved: '3 hours (vs. 8 hours manual)',
      },
      {
        name: 'Specification Package',
        trigger: 'Design phase completion',
        steps: ['Tesla drafts specifications', 'Newton checks standards', 'Ada organizes package', 'Final review'],
        output: 'Complete specification set',
        timeSaved: '1 day (vs. 3 days manual)',
      },
    ],

    templates: [
      { name: 'Technical Specification', type: 'Word', description: 'Equipment/system specs' },
      { name: 'RFI Response', type: 'Word', description: 'Request for information reply' },
      { name: 'Progress Report', type: 'Word', description: 'Monthly project status' },
      { name: 'Change Order', type: 'Word', description: 'Scope change documentation' },
      { name: 'Punch List', type: 'Excel', description: 'Completion tracking' },
      { name: 'O&M Manual', type: 'Word', description: 'Operations manual' },
      { name: 'Proposal Template', type: 'Word', description: 'Project proposal format' },
      { name: 'Meeting Minutes', type: 'Word', description: 'Project meeting documentation' },
    ],

    roiMetrics: [
      { metric: 'RFI response time', before: '6 hours', after: '2 hours', improvement: '67% faster' },
      { metric: 'Proposal creation', before: '1 week', after: '2 days', improvement: '70% faster' },
      { metric: 'Specification errors', before: '12%', after: '3%', improvement: '75% reduction' },
      { metric: 'Report preparation', before: '8 hours', after: '3 hours', improvement: '63% faster' },
    ],

    sampleCompany: {
      name: 'Apex Engineering Group',
      type: 'Multi-discipline engineering firm',
      size: '200 employees',
      structure: ['Civil Engineering (60)', 'Structural (45)', 'MEP (50)', 'Project Management (25)', 'Admin (20)'],
    },

    personas: [
      { name: 'Michael Chen', role: 'Principal Engineer', description: 'Reviews AI-assisted specifications', timeSaved: '6 hours/week' },
      { name: 'Sarah Johnson', role: 'Project Manager', description: 'Uses AI for status reports and RFIs', timeSaved: '10 hours/week' },
      { name: 'David Park', role: 'Design Engineer', description: 'AI assists with technical documentation', timeSaved: '12 hours/week' },
      { name: 'Lisa Martinez', role: 'Proposal Coordinator', description: 'Uses AI for proposal development', timeSaved: '15 hours/week' },
    ],

    ctaHeadline: 'Ready to transform engineering documentation?',
    ctaDescription: 'See how engineering firms save $25,000 per user annually with Templatean\'s AI document team.',
  },

  consulting: {
    slug: 'consulting',
    layout: 'dock-marquee',
    name: 'Consulting & Professional Services',
    shortName: 'Consulting',
    icon: Briefcase,
    color: '#4f46e5',
    gradient: 'from-indigo-600 to-blue-700',
    tier: 2,

    metaTitle: 'Consulting Document Automation | Templatean for Consultants',
    metaDescription: 'AI-powered proposals, deliverables, and client communications. 60% faster proposal creation. Built for consulting firms.',

    tagline: 'For Consulting Firms',
    headline: 'Every Proposal Compelling. Every Deliverable Polished. Every Client Impressed.',
    subheadline: 'AI agents handle strategy analysis, proposal writing, financial modeling, and client communications—reflecting your expertise.',
    heroStats: [
      { value: '60%', label: 'Faster proposal creation' },
      { value: '45%', label: 'Faster deliverables' },
      { value: '35%', label: 'Higher win rates' },
    ],

    marketSize: '$300 billion (US consulting)',
    documentIntensity: 'High (proposals + deliverables)',
    complianceRequirements: ['Client confidentiality', 'Industry-specific regulations'],

    painPoints: [
      {
        title: 'Proposal Development Time',
        impact: 'Days to create proposals',
        solution: 'AI accelerates proposal generation',
        icon: Clock,
      },
      {
        title: 'Deliverable Consistency',
        impact: 'Variable quality across teams',
        solution: 'AI ensures consistent formatting',
        icon: AlertCircle,
      },
      {
        title: 'Knowledge Reuse',
        impact: 'Recreating similar content',
        solution: 'AI leverages past work',
        icon: FileText,
      },
    ],

    aiAgents: [
      {
        name: 'Porter',
        role: 'Strategy Analyst',
        description: 'Market analysis, strategic frameworks, industry research',
        icon: Target,
        color: '#3B82F6',
        catchphrase: 'Strategic analysis complete',
      },
      {
        name: 'Drucker',
        role: 'Proposal Writer',
        description: 'Proposal narratives, methodology sections, case studies',
        icon: PenTool,
        color: '#EF4444',
        catchphrase: 'Proposal draft ready',
      },
      {
        name: 'Kaplan',
        role: 'Financial Analyst',
        description: 'Business cases, ROI analysis, financial models',
        icon: Calculator,
        color: '#8B5CF6',
        catchphrase: 'Financial analysis complete',
      },
      {
        name: 'Covey',
        role: 'Project Manager',
        description: 'Project plans, status reports, meeting documentation',
        icon: FolderOpen,
        color: '#F59E0B',
        catchphrase: 'Project status updated',
      },
      {
        name: 'Collins',
        role: 'Content Creator',
        description: 'Deliverable drafts, executive summaries, presentations',
        icon: Lightbulb,
        color: '#F97316',
        catchphrase: 'Content draft ready',
      },
      {
        name: 'Blanchard',
        role: 'Client Manager',
        description: 'Client communications, relationship documentation',
        icon: MessageSquare,
        color: '#14B8A6',
        catchphrase: 'Client update sent',
      },
    ],

    workflows: [
      {
        name: 'Proposal Development',
        trigger: 'RFP received',
        steps: ['Porter analyzes requirements', 'Drucker drafts narrative', 'Kaplan builds pricing', 'Final assembly'],
        output: 'Complete proposal package',
        timeSaved: '2 days (vs. 5 days manual)',
      },
      {
        name: 'Deliverable Creation',
        trigger: 'Project milestone',
        steps: ['Collins drafts content', 'Porter provides analysis', 'Kaplan adds financials', 'Review and polish'],
        output: 'Client-ready deliverable',
        timeSaved: '1 day (vs. 2 days manual)',
      },
      {
        name: 'Status Reporting',
        trigger: 'Weekly/monthly cycle',
        steps: ['Covey gathers project data', 'Collins writes summary', 'Blanchard prepares communication', 'Send to client'],
        output: 'Professional status update',
        timeSaved: '2 hours (vs. 4 hours manual)',
      },
      {
        name: 'Case Study Development',
        trigger: 'Project completion',
        steps: ['Porter summarizes approach', 'Collins writes narrative', 'Kaplan quantifies impact', 'Final formatting'],
        output: 'Marketing-ready case study',
        timeSaved: '4 hours (vs. 12 hours manual)',
      },
    ],

    templates: [
      { name: 'Consulting Proposal', type: 'Word', description: 'Client proposal format' },
      { name: 'Methodology Document', type: 'Word', description: 'Approach description' },
      { name: 'Project Charter', type: 'Word', description: 'Engagement kickoff' },
      { name: 'Status Report', type: 'Word', description: 'Progress update' },
      { name: 'Executive Summary', type: 'Word', description: 'Key findings summary' },
      { name: 'Business Case', type: 'Excel', description: 'ROI analysis model' },
      { name: 'Deliverable Template', type: 'PowerPoint', description: 'Client presentation' },
      { name: 'Case Study', type: 'Word', description: 'Success story format' },
    ],

    roiMetrics: [
      { metric: 'Proposal creation', before: '5 days', after: '2 days', improvement: '60% faster' },
      { metric: 'Deliverable production', before: '2 days', after: '1 day', improvement: '50% faster' },
      { metric: 'Proposal win rate', before: '25%', after: '34%', improvement: '36% increase' },
      { metric: 'Knowledge reuse', before: '20%', after: '70%', improvement: '250% increase' },
    ],

    sampleCompany: {
      name: 'Strategos Consulting',
      type: 'Management consulting',
      size: '120 employees',
      structure: ['Partners (12)', 'Principals (20)', 'Managers (35)', 'Consultants (40)', 'Analysts (13)'],
    },

    personas: [
      { name: 'Jennifer Chen', role: 'Partner', description: 'Reviews AI-generated proposals and deliverables', timeSaved: '6 hours/week' },
      { name: 'Michael Park', role: 'Manager', description: 'Uses AI for deliverable development', timeSaved: '10 hours/week' },
      { name: 'Sarah Wilson', role: 'Consultant', description: 'AI assists with research and analysis', timeSaved: '12 hours/week' },
      { name: 'David Lee', role: 'Proposal Lead', description: 'Uses AI for proposal coordination', timeSaved: '15 hours/week' },
    ],

    ctaHeadline: 'Ready to transform consulting delivery?',
    ctaDescription: 'See how consulting firms save $35,000 per consultant annually with Templatean\'s AI document team.',
  },
}

export const industriesList = Object.values(industriesData)
export const industriesByTier = {
  1: industriesList.filter(i => i.tier === 1),
  2: industriesList.filter(i => i.tier === 2),
  3: industriesList.filter(i => i.tier === 3),
}
